import { BaseModel, type RelationConfig } from './base';

// Enums

export enum FormItemType {
  section = 0,
  paragraph = 1,
  field = 2,
}

export const FormItemTypeLabels: Record<FormItemType, string> = {
  [FormItemType.section]: 'form.section',
  [FormItemType.paragraph]: 'general.paragraph',
  [FormItemType.field]: 'form.field',
};

export enum FormItemDataType {
  text = 0,
  longtext = 1,
  multiple = 2,
  bool = 3,
  number = 4,
  date = 5,
  signature = 6,
  yesNo = 7,
  multiSelect = 8,
  annotation = 9,
}

export const FormItemDataTypeLabels: Record<FormItemDataType, string> = {
  [FormItemDataType.text]: 'field.text',
  [FormItemDataType.longtext]: 'field.longText',
  [FormItemDataType.multiple]: 'field.multiple',
  [FormItemDataType.bool]: 'field.checkbox',
  [FormItemDataType.number]: 'field.number',
  [FormItemDataType.date]: 'field.date',
  [FormItemDataType.signature]: 'field.signature',
  [FormItemDataType.yesNo]: 'field.yesNo',
  [FormItemDataType.multiSelect]: 'field.multiSelect',
  [FormItemDataType.annotation]: 'field.annotation',
};

export enum FormItemDisplay {
  list = 'list',
  radio = 'radio',
}

export const FormItemDisplayLabels: Record<FormItemDisplay, string> = {
  [FormItemDisplay.list]: 'form.dropdown',
  [FormItemDisplay.radio]: 'form.radio',
};

// Helper Classes

export class FormItemOptions extends BaseModel {
  private _displayValue?: FormItemDisplay;

  // 'display' conflicts with BaseModel.display, so keep the API value behind a
  // writable accessor and restore the original key when serializing.
  constructor(data?: any) {
    const { display: displayValue, ...rest } = data || {};
    super(rest);
    if (displayValue !== undefined) {
      this._displayValue = displayValue;
    }
  }

  override get display(): string {
    return this._displayValue ?? '';
  }

  override set display(value: string) {
    this._displayValue = value as FormItemDisplay;
  }

  getDisplayValue(): FormItemDisplay | undefined {
    return this._displayValue;
  }

  override toJSON(): Record<string, any> {
    const data = super.toJSON();

    delete data._displayValue;

    if (this._displayValue !== undefined) {
      data.display = this._displayValue;
    }

    return data;
  }

  get isDisplayList(): boolean {
    return this.getDisplayValue() === FormItemDisplay.list;
  }

  get isDisplayRadio(): boolean {
    return this.getDisplayValue() === FormItemDisplay.radio;
  }
}

// Main Model

export class FormItem extends BaseModel {
  idFormItem!: number;
  type!: FormItemType;
  dataType!: FormItemDataType;
  options!: FormItemOptions | null;

  constructor(data?: any) {
    super(data);

    if (this.dataType === FormItemDataType.annotation) {
      const options = this.options instanceof FormItemOptions
        ? this.options.toJSON()
        : (this.options && typeof this.options === 'object' ? this.options : {});

      const draw = options.draw && typeof options.draw === 'object' ? options.draw : {};
      const stamps = options.stamps && typeof options.stamps === 'object' ? options.stamps : {};
      const erase = options.erase && typeof options.erase === 'object' ? options.erase : {};

      this.options = new FormItemOptions({
        ...options,
        draw: {
          ...draw,
          enabled: typeof draw.enabled === 'boolean' ? draw.enabled : true,
          colours: Array.isArray(draw.colours) ? draw.colours : ['#000000'],
        },
        stamps: {
          ...stamps,
          enabled: typeof stamps.enabled === 'boolean' ? stamps.enabled : false,
          stamps: Array.isArray(stamps.stamps) ? stamps.stamps : [],
        },
        erase: {
          ...erase,
          enabled: typeof erase.enabled === 'boolean' ? erase.enabled : true,
        },
      });
    }
  }

  static override relations(): RelationConfig {
    return {
      options: { type: FormItemOptions },
    };
  }

  override objectID(): string | null {
    const id = Number(this.idFormItem);

    return id > 0 ? id.toString() : null;
  }

  get isTypeSection(): boolean {
    return this.type === FormItemType.section;
  }

  get isTypeParagraph(): boolean {
    return this.type === FormItemType.paragraph;
  }

  get isTypeField(): boolean {
    return this.type === FormItemType.field;
  }

  get isDataTypeText(): boolean {
    return this.dataType === FormItemDataType.text;
  }

  get isDataTypeLongText(): boolean {
    return this.dataType === FormItemDataType.longtext;
  }

  get isDataTypeMultiple(): boolean {
    return this.dataType === FormItemDataType.multiple;
  }

  get isDataTypeBool(): boolean {
    return this.dataType === FormItemDataType.bool;
  }

  get isDataTypeNumber(): boolean {
    return this.dataType === FormItemDataType.number;
  }

  get isDataTypeDate(): boolean {
    return this.dataType === FormItemDataType.date;
  }

  get isDataTypeSignature(): boolean {
    return this.dataType === FormItemDataType.signature;
  }

  get isDataTypeAnnotation(): boolean {
    return this.dataType === FormItemDataType.annotation;
  }

  get isDataTypeMultiSelect(): boolean {
    return this.dataType === FormItemDataType.multiSelect;
  }

  get isDisplayList(): boolean {
    return this.options?.isDisplayList ?? false;
  }

  get isDisplayRadio(): boolean {
    return this.options?.isDisplayRadio ?? false;
  }
}
